import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCan } from "@/auth/permissions";
import {
  CTRIP_PROFILE_QUERY_KEY, ctripLookupsApi, NAME_RULE_LABEL, PRODUCT_TYPES, TOUR_TYPES, type CtripProfile, type NameRuleType,
} from "@/services/ctripLookups";

/**
 * CTRIP-P1 · 渠道中心 › 携程 › 「携程资料」标签（样稿 携程价库直连-样稿.html ⑤）。
 * 全公司一份，所有携程产品共用。空着的允许保存——缺项在建档预检（P2）里报，这里不拿默认值顶替。
 * 接口 GET / PUT /api/v1/integrations/platforms/ctrip/profile（权限沿用渠道中心：看 = platforms.view，改 = platforms.manage）。
 */

const TOUR_LABEL: Record<string, string> = { AroundTour: "国内短途", DomesticTour: "国内长途", OutboundTour: "出境长途", OverSeasLocalTour: "出境短途" };
const PRODUCT_LABEL: Record<string, string> = { Retail: "零售", Self: "自营" };

interface Form {
  contract_id: string; brand_name: string;
  complain_name: string; complain_mobile: string; booking_name: string; booking_mobile: string;
  bookable: "" | "yes" | "no"; age_min: string; age_max: string; tour_type: string; product_type: string;
  rules: { type: NameRuleType; id: string }[];
}

const toForm = (p: CtripProfile): Form => ({
  contract_id: p.contract_id == null ? "" : String(p.contract_id), brand_name: p.brand_name ?? "",
  complain_name: p.complain_contact.name ?? "", complain_mobile: p.complain_contact.mobile ?? "",
  booking_name: p.booking_contact.name ?? "", booking_mobile: p.booking_contact.mobile ?? "",
  bookable: p.child.bookable == null ? "" : p.child.bookable ? "yes" : "no",
  age_min: p.child.age_min == null ? "" : String(p.child.age_min), age_max: p.child.age_max == null ? "" : String(p.child.age_max),
  tour_type: p.tour_type ?? "", product_type: p.product_type ?? "",
  rules: (p.name_rules ?? []).map((r) => ({ type: r.type, id: String(r.ctrip_id) })),
});

const blank = (s: string) => (s.trim() === "" ? null : s.trim());
const num = (s: string): number | null => {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isNaN(n) ? (s.trim() as unknown as number) : n;   // 乱填的原文交给后端报 422，不静默变 null
};
const toBody = (f: Form): CtripProfile => ({
  contract_id: num(f.contract_id), brand_name: blank(f.brand_name),
  complain_contact: { name: blank(f.complain_name), mobile: blank(f.complain_mobile) },
  booking_contact: { name: blank(f.booking_name), mobile: blank(f.booking_mobile) },
  child: { bookable: f.bookable === "" ? null : f.bookable === "yes", age_min: num(f.age_min), age_max: num(f.age_max) },
  tour_type: blank(f.tour_type), product_type: blank(f.product_type),
  name_rules: f.rules.filter((r) => r.id.trim() !== "").map((r) => ({ type: r.type, ctrip_id: num(r.id) as number })),
});

const inp = "w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[13px] disabled:bg-slate-50 disabled:text-slate-500";

export function CtripProfilePanel() {
  const qc = useQueryClient();
  const canEdit = useCan("api_integration.platforms.manage");
  const q = useQuery({ queryKey: CTRIP_PROFILE_QUERY_KEY, queryFn: ctripLookupsApi.getProfile });
  const [f, setF] = useState<Form | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (q.data) setF(toForm(q.data)); }, [q.data]);

  const save = useMutation({
    mutationFn: () => ctripLookupsApi.putProfile(toBody(f!)),
    onSuccess: (p) => { setErr(null); setSaved(true); qc.setQueryData(CTRIP_PROFILE_QUERY_KEY, p); },
    onError: (e) => { setSaved(false); setErr((e as Error).message || "保存失败"); },
  });

  if (q.isLoading) return <div className="p-6 text-slate-500">加载中...</div>;
  if (q.isError || !f) {
    return <button type="button" onClick={() => q.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100">携程资料加载失败，点击重试</button>;
  }
  const set = <K extends keyof Form>(k: K, v: Form[K]) => { setSaved(false); setF({ ...f, [k]: v }); };
  const req = (v: string) => (v.trim() === "" ? "border-rose-300 bg-rose-50/40" : "");

  return (
    <div className="rounded-[14px] border border-slate-200 bg-white p-5" data-testid="ctrip-profile-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-[15px] font-semibold text-slate-900">携程资料</h3>
        <span className="text-[12px] text-slate-400">空着的都是携程必填，问签约同事 / 携程拿</span>
        <div className="ml-auto flex items-center gap-2">
          {saved && <span role="status" className="text-[12px] text-emerald-600">已保存</span>}
          {canEdit
            ? <button type="button" data-testid="ctrip-profile-save" disabled={save.isPending} onClick={() => save.mutate()} className="rounded-md bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{save.isPending ? "保存中…" : "保存"}</button>
            : <span className="text-[12px] text-slate-400">只有老板 / 技术管理员可以修改</span>}
        </div>
      </div>
      {err && <div role="alert" className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">保存失败：{err}</div>}

      <div className="grid grid-cols-1 items-center gap-x-4 gap-y-3 sm:grid-cols-[150px_minmax(0,1fr)]">
        <label htmlFor="cp-contract" className="text-[13px] text-slate-500">携程合同号</label>
        <input id="cp-contract" inputMode="numeric" disabled={!canEdit} value={f.contract_id} onChange={(e) => set("contract_id", e.target.value)} placeholder="没填 · 向携程签约同事要" className={`${inp} ${req(f.contract_id)}`} />

        <label htmlFor="cp-brand" className="text-[13px] text-slate-500">在携程的品牌名</label>
        <input id="cp-brand" disabled={!canEdit} value={f.brand_name} onChange={(e) => set("brand_name", e.target.value)} className={`${inp} ${req(f.brand_name)}`} />

        <span className="text-[13px] text-slate-500">投诉联系人</span>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input aria-label="投诉联系人姓名" placeholder="姓名" disabled={!canEdit} value={f.complain_name} onChange={(e) => set("complain_name", e.target.value)} className={`${inp} ${req(f.complain_name)}`} />
          <input aria-label="投诉联系人手机" placeholder="手机，如 +1 702-555-0188" disabled={!canEdit} value={f.complain_mobile} onChange={(e) => set("complain_mobile", e.target.value)} className={`${inp} ${req(f.complain_mobile)}`} />
        </div>

        <span className="text-[13px] text-slate-500">预订联系人</span>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input aria-label="预订联系人姓名" placeholder="姓名" disabled={!canEdit} value={f.booking_name} onChange={(e) => set("booking_name", e.target.value)} className={`${inp} ${req(f.booking_name)}`} />
          <input aria-label="预订联系人手机" placeholder="手机，如 +1 702-555-0166" disabled={!canEdit} value={f.booking_mobile} onChange={(e) => set("booking_mobile", e.target.value)} className={`${inp} ${req(f.booking_mobile)}`} />
        </div>

        <span className="text-[13px] text-slate-500">儿童能不能订</span>
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-slate-600">
          <select aria-label="儿童能不能订" disabled={!canEdit} value={f.bookable} onChange={(e) => set("bookable", e.target.value as Form["bookable"])} className={`${inp} !w-auto ${f.bookable === "" ? "border-rose-300 bg-rose-50/40" : ""}`}>
            <option value="">没填</option><option value="yes">能订</option><option value="no">不能订</option>
          </select>
          {f.bookable === "yes" && (
            <>
              年龄
              <input aria-label="儿童最小年龄" inputMode="numeric" disabled={!canEdit} value={f.age_min} onChange={(e) => set("age_min", e.target.value)} className={`${inp} !w-16`} />
              岁到
              <input aria-label="儿童最大年龄" inputMode="numeric" disabled={!canEdit} value={f.age_max} onChange={(e) => set("age_max", e.target.value)} className={`${inp} !w-16`} />
              岁（不含上限）· 价格 = 成人
            </>
          )}
        </div>

        <span className="text-[13px] text-slate-500">线路类型 / 产品类型</span>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <select aria-label="线路类型" disabled={!canEdit} value={f.tour_type} onChange={(e) => set("tour_type", e.target.value)} className={inp}>
            <option value="">等携程答复（问题 8）后填</option>
            {TOUR_TYPES.map((t) => <option key={t} value={t}>{TOUR_LABEL[t]}（{t}）</option>)}
          </select>
          <select aria-label="产品类型" disabled={!canEdit} value={f.product_type} onChange={(e) => set("product_type", e.target.value)} className={inp}>
            <option value="">没填</option>
            {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{PRODUCT_LABEL[t]}（{t}）</option>)}
          </select>
        </div>

        <span className="pt-1.5 text-[13px] text-slate-500">携程目的地规则</span>
        <div className="flex flex-col gap-2" data-testid="name-rules">
          {f.rules.length === 0 && <span className="text-[12.5px] text-rose-600">没填 · 携程建档必填：至少一条国家 / 省份 / 景区的携程 ID</span>}
          {f.rules.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select aria-label={`规则类型 ${i + 1}`} disabled={!canEdit} value={r.type} onChange={(e) => set("rules", f.rules.map((x, j) => (j === i ? { ...x, type: e.target.value as NameRuleType } : x)))} className={`${inp} !w-24`}>
                {(Object.keys(NAME_RULE_LABEL) as NameRuleType[]).map((t) => <option key={t} value={t}>{NAME_RULE_LABEL[t]}</option>)}
              </select>
              <input aria-label={`规则携程ID ${i + 1}`} inputMode="numeric" placeholder="携程 ID" disabled={!canEdit} value={r.id} onChange={(e) => set("rules", f.rules.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)))} className={`${inp} !w-40`} />
              {canEdit && <button type="button" onClick={() => set("rules", f.rules.filter((_, j) => j !== i))} className="text-[12px] text-slate-400 hover:underline">删除</button>}
            </div>
          ))}
          {canEdit && <button type="button" data-testid="add-name-rule" onClick={() => set("rules", [...f.rules, { type: "country", id: "" }])} className="w-fit text-[12.5px] font-medium text-blue-600 hover:underline">+ 加一条</button>}
        </div>
      </div>
      <div className="mt-3 text-[11.5px] text-slate-400">手机号姓名分开填；任何一项空着，建档预检里所有携程产品都会显示「携程资料缺项」。</div>
    </div>
  );
}
